import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store/store";

/**
 * Custom hook to manage all Redux selectors needed for filter generation.
 * Separates selector logic from business logic for better testability.
 *
 * @returns Object containing:
 * - `postgresFacetResults` — org-wide facet counts from `postgresFacetCounts.unique_filter_options`
 *   (populated by `getUniqueFilterOptions` inside `refreshPostgresFacetCounts`; the return property
 *   name is legacy for downstream filter builders).
 * - `isPostgresFacetResultsLoading` — `postgresFacetCounts.is_facet_results_loading` (loading flag
 *   for the assignee-scoped sidebar facet fetch, dispatched in the same refresh batch).
 * - `authStatusOptions`, `boOptions`, `estimateOptions` (Firestore `estimate_options`),
 *   `providerDetails`, `allProvidersDetails` from their slices.
 */
export const useFilterDataSelectors = () => {
  /** `unique_filter_options` from Redux, exposed as `postgresFacetResults` for callers. */
  const {
    unique_filter_options: postgresFacetResults,
    is_facet_results_loading: isPostgresFacetResultsLoading,
  } = useSelector((state: RootState) => state.postgresFacetCounts);

  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );

  const { data: boOptions } = useSelector(
    (state: RootState) => state.boOptions,
  );

  const { data: estimateOptions } = useSelector(
    (state: RootState) => state.estimateOptions,
  );

  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );

  const { data: allProvidersDetails } = useSelector(
    (state: RootState) => state.allProviders,
  );

  return {
    postgresFacetResults,
    isPostgresFacetResultsLoading,
    authStatusOptions,
    boOptions,
    estimateOptions,
    providerDetails,
    allProvidersDetails,
  };
};
